import { FunctionComponent, memo, useMemo } from "react";
import { ILanguage } from "@edgetier/watchtower-types";
import { IProps } from "./select-language.types";
import { useLanguages } from "hooks-for/filters/use-languages";
import FilteredSelectField from "../filtered-select-field";
import "./select-language.scss";

/**
 * Filter for language.
 * @param props.mustInclude A language ID that has to be included in the select menu.
 * @param props.name        Field name.
 * @param props.parameters  Parameters to send along with the request for languages.
 * @returns Language select.
 */
const SelectLanguage: FunctionComponent<IProps> = ({
    mustInclude,
    name = "languageIsoCode",
    parameters,
    ...otherProps
}) => {
    const { data: allLanguages, isLoading: areAllLanguagesLoading } = useLanguages();

    const selectedLanguage = useMemo(() => {
        return (allLanguages ?? []).filter(({ languageIsoCode }) => languageIsoCode === mustInclude);
    }, [allLanguages, mustInclude]);

    const { data: filteredLanguages, isLoading: areFilteredLanguagesLoading } = useLanguages(parameters, {
        enabled: typeof parameters !== "undefined",
        select: (languages) => {
            // Make sure any language ID in the URL is included in the options.
            const hasSelectedLanguage = languages.some(({ languageIsoCode }) => languageIsoCode === mustInclude);
            if (!hasSelectedLanguage) {
                return [...languages, ...selectedLanguage];
            }

            return languages;
        },
    });

    return (
        <FilteredSelectField<ILanguage>
            description="language"
            getLabel={({ language }) => language}
            getValue={({ languageIsoCode }) => languageIsoCode}
            isLoading={areAllLanguagesLoading || areFilteredLanguagesLoading}
            allOptions={allLanguages ?? []}
            filteredOptions={filteredLanguages ?? []}
            name={name}
            {...otherProps}
        />
    );
};

export default memo(SelectLanguage);
